import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { AlertTriangle, CalendarCheck } from "lucide-react";
import { api } from "../../../api";
import { DESK } from "../../../constants/testIds";
import { useDeskData } from "../../../contexts/DeskDataContext";
import { formatCurrency } from "../../../lib/format";
import { fadeUp } from "../../../lib/motion";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { ErrorState } from "../../../components/common/ErrorState";
import { GymLoader } from "../../../components/common/GymLoader";

function todayIso() { return new Date().toISOString().slice(0, 10); }

// UX-FD-03 §1 SCR-P02-90 + highest-risk rule #5: mismatch is shown PLAINLY (dot+pill row, never
// hidden or auto-zeroed) whenever `total_variance !== 0`. `GET /reports/day-end/:branchId` is a
// stateless read-model (Prompt 09's own documented design: "no collection exists for persisting
// recon records") — there is no "close the day" write anywhere on this backend, so that action
// is flagged below rather than wired to a button that would silently do nothing. Per-shift line
// items beyond the mismatched-shift id list aren't available either (the rollup only returns an
// aggregate `total_variance`, the same documented limitation as the Owner Cockpit's own
// Needs-Attention panel from UX-01).
export default function DayEnd() {
  const { branchId } = useDeskData();
  const reducedMotion = useReducedMotion();
  const [date, setDate] = useState(todayIso());
  const [loadedDate, setLoadedDate] = useState(todayIso());

  const dayEndQ = useQuery({
    queryKey: ["desk-mgr-day-end", branchId, loadedDate],
    queryFn: async () => (await api.get(`/reports/day-end/${branchId}`, { params: { date: loadedDate } })).data,
    enabled: !!branchId,
  });

  const hasMismatch = (dayEndQ.data?.total_variance ?? 0) !== 0;

  return (
    <div data-testid={DESK.mgrDayEndPage} className="space-y-6 max-w-2xl">
      <motion.div {...fadeUp(reducedMotion)}>
        <h1 className="text-2xl font-bold" style={{ color: "#141416" }}>Day-end reconciliation</h1>
        <p className="text-sm mt-0.5" style={{ color: "#68686E" }}>Rolls up every shift handover for the branch on this date.</p>
      </motion.div>

      <motion.form {...fadeUp(reducedMotion, 0.03)} onSubmit={(e) => { e.preventDefault(); setLoadedDate(date); }} className="flex gap-2">
        <Input data-testid={DESK.mgrDayEndDateInput} type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        <Button type="submit" data-testid={DESK.mgrDayEndLoadButton} className="text-white shrink-0" style={{ background: "#141416" }}>Load</Button>
      </motion.form>

      {dayEndQ.isLoading && <GymLoader label="Loading day-end rollup…" testId="desk-mgr-dayend-loader" />}
      {dayEndQ.isError && <ErrorState error={dayEndQ.error} onRetry={() => dayEndQ.refetch()} title="Couldn't load day-end rollup" />}

      {dayEndQ.data && (
        <motion.div {...fadeUp(reducedMotion, 0.06)} className="rounded-[14px] p-5 space-y-3" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><p className="text-xs" style={{ color: "#68686E" }}>Shifts closed</p><p className="font-semibold" style={{ color: "#141416" }}>{dayEndQ.data.shift_count}</p></div>
            <div><p className="text-xs" style={{ color: "#68686E" }}>Float opened</p><p className="font-semibold" style={{ color: "#141416" }}>{formatCurrency(dayEndQ.data.total_float)}</p></div>
            <div><p className="text-xs" style={{ color: "#68686E" }}>Expected cash</p><p className="font-semibold" style={{ color: "#141416" }}>{formatCurrency(dayEndQ.data.total_expected)}</p></div>
            <div><p className="text-xs" style={{ color: "#68686E" }}>Counted cash</p><p className="font-semibold" style={{ color: "#141416" }}>{formatCurrency(dayEndQ.data.total_counted)}</p></div>
          </div>
          <div
            data-testid={DESK.mgrDayEndMismatchFlag}
            className="flex items-center gap-2 rounded-[10px] px-3 py-2 text-sm font-medium"
            style={hasMismatch ? { background: "var(--status-overdue-bg)", color: "var(--status-overdue-text)" } : { background: "#F4F3EF", color: "#141416" }}
          >
            {hasMismatch ? <AlertTriangle size={14} /> : <CalendarCheck size={14} />}
            {hasMismatch
              ? `Variance of ${formatCurrency(Math.abs(dayEndQ.data.total_variance))} across ${dayEndQ.data.mismatched_shifts.length} shift(s) — needs a look.`
              : "No cash variance across any shift today."}
          </div>
          {dayEndQ.data.day_pnl && (
            <div className="grid grid-cols-3 gap-3 text-sm pt-2" style={{ borderTop: "1px solid #EBEAE5" }}>
              <div><p className="text-xs" style={{ color: "#68686E" }}>Revenue</p><p className="font-semibold" style={{ color: "#141416" }}>{formatCurrency(dayEndQ.data.day_pnl.revenue)}</p></div>
              <div><p className="text-xs" style={{ color: "#68686E" }}>Expense</p><p className="font-semibold" style={{ color: "#141416" }}>{formatCurrency(dayEndQ.data.day_pnl.expense)}</p></div>
              <div><p className="text-xs" style={{ color: "#68686E" }}>Profit</p><p className="font-semibold" style={{ color: "#141416" }}>{formatCurrency(dayEndQ.data.day_pnl.profit)}</p></div>
            </div>
          )}
          <p className="text-xs italic" style={{ color: "#9A9A9E" }}>This is a live, re-viewable read-model — there's no "close the day" write on this backend yet (no such state exists to persist), so nothing here is a one-way action.</p>
        </motion.div>
      )}
    </div>
  );
}
